// ABOUTME: One line naming what a screen or node is assigned to: the target's icon, "Label: value" and an optional action.
// ABOUTME: Presentational; the icon and label come from the shared target map (the dealership line of catalogue and cart).

import { Box, Button, Stack, Typography } from '@ds/coolshop';
import { useIntl } from 'react-intl';

import type { AssignTarget } from '../api/assignmentApi';
import { ASSIGNMENT_TARGET_ICONS, ASSIGNMENT_TARGET_LABELS } from '../utils/assignmentTargets';

type AssignmentLineProps = {
    target: AssignTarget;
    /** what the target is assigned to, already worded (a name, "None (company)") */
    value: string;
    /** the action at the line's end, e.g. "Change" */
    action?: { label: string; onClick: () => void };
    /** takes its container's width and puts the action at its far end, as in the cart's header */
    actionAtEnd?: boolean;
    /**
     * centres the icon on a column of this width from the line's start, lining it up with the images
     * of a list below (the cart's lines); the text keeps following the icon
     */
    iconColumnWidth?: string;
};

export function AssignmentLine({
    target,
    value,
    action,
    actionAtEnd = false,
    iconColumnWidth,
}: AssignmentLineProps) {
    const { formatMessage } = useIntl();
    const Icon = ASSIGNMENT_TARGET_ICONS[target];

    return (
        <Stack
            direction="row"
            // The text and the button's label share a baseline; the icon is centred on the line.
            alignItems="baseline"
            justifyContent={actionAtEnd ? 'space-between' : 'flex-start'}
            gap={1}
            width={actionAtEnd ? '100%' : undefined}
            color="text.secondary"
            data-testid={`assignment-line-${target}`}
        >
            <Stack direction="row" alignItems="baseline" gap={1} minWidth={0}>
                <Box
                    alignSelf="center"
                    display="flex"
                    // The small icon is 20px wide.
                    marginLeft={
                        iconColumnWidth ? `calc((${iconColumnWidth} - 20px) / 2)` : undefined
                    }
                >
                    <Icon fontSize="small" />
                </Box>
                <Typography variant="body2">
                    {`${formatMessage(ASSIGNMENT_TARGET_LABELS[target])}: ${value}`}
                </Typography>
            </Stack>
            {action && (
                <Button variant="contained" size="small" color="primary" onClick={action.onClick}>
                    {action.label}
                </Button>
            )}
        </Stack>
    );
}

export default AssignmentLine;
